import { CommonModule } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    ElementRef,
    forwardRef,
    inject,
    input,
    output,
    signal,
    untracked,
    ViewChild,
    ViewEncapsulation
} from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import {
    MultiSelect,
    MultiSelectChangeEvent,
    MultiSelectFilterEvent,
    MultiSelectLazyLoadEvent,
    MultiSelectModule,
    MultiSelectRemoveEvent,
    MultiSelectSelectAllChangeEvent
} from 'primeng/multiselect';
import { FcpFloatLabelComponent, FloatLabelVariant } from '../fcp-float-label';

/** Spec max overlay width (no semantic size token yet). */
const OVERLAY_MAX_WIDTH_PX = 480;

@Component({
    selector: 'fcp-multiselect',
    imports: [CommonModule, MultiSelectModule, FormsModule, FcpFloatLabelComponent],
    styleUrl: './fcp-multiselect.component.less',
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        '[attr.data-e2e-hook]': 'e2e()',
        '[class.is-loading]': 'loading()'
    },
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => FcpMultiSelectComponent),
            multi: true
        }
    ],
    template: `
        <ng-template #multiselectTemplate>
            <p-multiselect
                [options]="options()"
                [placeholder]="showFloatLabel() ? '' : placeholder()"
                [optionLabel]="optionLabel()"
                [optionValue]="optionValue()"
                [optionDisabled]="optionDisabled()"
                [optionGroupLabel]="optionGroupLabel()"
                [optionGroupChildren]="optionGroupChildren()"
                [group]="group()"
                [filter]="filter()"
                [filterMatchMode]="filterMatchMode()"
                [filterLocale]="filterLocale()"
                [scrollHeight]="scrollHeight()"
                [defaultLabel]="defaultLabel()"
                [selectedItemsLabel]="selectedItemsLabel()"
                [maxSelectedLabels]="maxSelectedLabels()"
                [display]="display()"
                [selectionLimit]="selectionLimit()"
                [showToggleAll]="isToggleAllVisible()"
                [selectAll]="selectAll()"
                [showHeader]="isHeaderVisible()"
                [showClear]="showClear()"
                [resetFilterOnHide]="resetFilterOnHide()"
                [dropdownIcon]="dropdownIcon()"
                [loading]="loading()"
                [loadingIcon]="loadingIcon()"
                [lazy]="lazy()"
                [virtualScroll]="virtualScroll()"
                [virtualScrollItemSize]="virtualScrollItemSize()"
                [virtualScrollOptions]="virtualScrollOptions()"
                [emptyFilterMessage]="emptyFilterMessage()"
                [emptyMessage]="emptyMessage()"
                [disabled]="isDisabled()"
                [readonly]="readonly()"
                [required]="required()"
                [tabindex]="tabindex()"
                [inputId]="inputId() || id()"
                [ariaLabel]="ariaLabel()"
                [ariaLabelledBy]="ariaLabelledBy()"
                [style]="style()"
                [styleClass]="styleClass()"
                [panelStyle]="resolvedPanelStyle()"
                [panelStyleClass]="resolvedPanelStyleClass()"
                [appendTo]="appendTo()"
                [dataKey]="dataKey()"
                [name]="name()"
                [autofocus]="autofocus()"
                [overlayVisible]="overlayVisible()"
                [filterBy]="filterBy()"
                [ngModel]="model()"
                [fluid]="fluid()"
                [size]="size()"
                (ngModelChange)="handleChange($event)"
                (onChange)="onChange.emit($event)"
                (onFocus)="onFocus.emit($event)"
                (onBlur)="handleBlur($event)"
                (onPanelShow)="handlePanelShow()"
                (onPanelHide)="onHide.emit()"
                (onFilter)="handleFilter($event)"
                (onSelectAllChange)="onSelectAllChange.emit($event)"
                (onLazyLoad)="onLazyLoad.emit($event)"
                (onClear)="onClear.emit()"
                (onRemove)="onRemove.emit($event)"
            />
        </ng-template>

        @if (showFloatLabel()) {
            <fcp-float-label [variant]="floatLabelVariant">
                <ng-container *ngTemplateOutlet="multiselectTemplate" />
                <label [for]="inputId() || id()">{{ placeholder() }}</label>
            </fcp-float-label>
        } @else {
            <ng-container *ngTemplateOutlet="multiselectTemplate" />
        }
    `
})
export class FcpMultiSelectComponent implements ControlValueAccessor {
    private readonly _virtualScrollItemSize = 36;
    private readonly _scrollHeight = '200px';
    private readonly _maxSelectedLabels = 3;
    readonly floatLabelVariant = FloatLabelVariant.In;

    private readonly host = inject(ElementRef<HTMLElement>);
    @ViewChild(MultiSelect) private multiSelectRef?: MultiSelect;

    /**
     * Measured overlay width from the longest option label (+ chrome). At least the trigger
     * width; may grow up to 480px when the trigger is narrower. Falls back to the trigger
     * when a wider panel would not fit left-aligned under the field. Virtual-scroll rows are
     * absolutely positioned so CSS max-content cannot discover long labels on its own.
     */
    private readonly overlayContentWidthPx = signal<number | null>(null);
    /**
     * Virtual-scroller viewport height: `min(scrollHeight, optionCount * itemSize)`.
     * Shrinks for short lists; empty lists use one item-size row for the empty message.
     */
    private readonly overlayListHeightPx = signal<number | null>(null);

    constructor() {
        // appendTo defaults to 'body', so an already-open panel isn't covered by the
        // host's dimmed/pointer-events-none loading style — close it explicitly instead.
        effect(() => {
            if (this.loading()) {
                this.multiSelectRef?.hide();
            }
        });

        effect(() => {
            const options = this.options();
            const optionLabel = this.optionLabel();
            const group = this.group();
            const optionGroupChildren = this.optionGroupChildren();
            const optionGroupLabel = this.optionGroupLabel();
            const itemSize = this.virtualScrollItemSize();
            const scrollHeight = this.scrollHeight();
            untracked(() => {
                queueMicrotask(() =>
                    this.syncOverlaySize(
                        options,
                        optionLabel,
                        group,
                        optionGroupChildren,
                        optionGroupLabel,
                        itemSize,
                        scrollHeight
                    )
                );
            });
        });
    }

    e2e = input<string | null>(null);
    options = input<unknown[]>([]);
    placeholder = input<string>('');
    optionLabel = input<string>('label');
    optionValue = input<string>('value');
    optionDisabled = input<string>('');
    optionGroupLabel = input<string>('label');
    optionGroupChildren = input<string>('items');
    group = input<boolean>(false);
    filter = input<boolean>(true);
    filterMatchMode = input<MultiSelectFilterMatchMode>(MultiSelectFilterMatchMode.Contains);
    filterLocale = input<string | undefined>();
    scrollHeight = input<string>(this._scrollHeight);
    defaultLabel = input<string>('');
    selectedItemsLabel = input<string>('');
    maxSelectedLabels = input<number>(this._maxSelectedLabels);
    display = input<MultiSelectDisplay>(MultiSelectDisplay.Comma);
    selectionLimit = input<number>(0);
    showToggleAll = input<boolean>(true);
    selectAll = input<boolean | null>(null);
    showHeader = input<boolean>(true);
    showClear = input<boolean>(false);
    resetFilterOnHide = input<boolean>(true);
    floatLabel = input<boolean>(false);
    // Unset so PrimeNG draws the same chevron-down SVG as fcp-select. A class string still replaces it.
    dropdownIcon = input<string | undefined>(undefined);
    loading = input<boolean>(false);
    loadingIcon = input<string>('');
    lazy = input<boolean>(false);
    virtualScroll = input<boolean>(false);
    virtualScrollItemSize = input<number>(this._virtualScrollItemSize);
    virtualScrollOptions = input<Record<string, unknown>>();
    emptyFilterMessage = input<string>('');
    emptyMessage = input<string>('');
    disabled = input<boolean>(false);
    readonly = input<boolean>(false);
    required = input<boolean>(false);
    tabindex = input<number>(0);
    inputId = input<string>('');
    ariaLabel = input<string>('');
    ariaLabelledBy = input<string>('');
    style = input<Record<string, string | number>>();
    panelStyle = input<Record<string, string | number>>();
    panelStyleClass = input<string>('');
    appendTo = input<string | HTMLElement>('body');
    dataKey = input<string>('');
    name = input<string>('');
    autofocus = input<boolean>(false);
    overlayVisible = input<boolean>(false);
    filterBy = input<string>('');
    fluid = input<boolean>(false);
    id = input<string | undefined>(undefined);
    size = input<MultiSelectSizes>(MultiSelectSizes.Normal);

    readonly showFloatLabel = computed(() => this.floatLabel() && this.size() !== MultiSelectSizes.Small);
    // Header hosts the search box and select-all checkbox; hide both when filter is off.
    readonly isHeaderVisible = computed(() => this.filter() && this.showHeader());
    readonly isToggleAllVisible = computed(() => this.filter() && this.showToggleAll());
    readonly resolvedPanelStyleClass = computed(() => this.panelStyleClass().trim());
    /**
     * Overlay panel styles:
     * - list height from option count (shrinks for short lists; caps at scrollHeight)
     * - content width from measured longest option (virtual-scroll cannot size via max-content)
     */
    readonly resolvedPanelStyle = computed(() => {
        const style: Record<string, string | number> = {
            ...this.panelStyle()
        };
        const contentWidth = this.overlayContentWidthPx();
        if (contentWidth != null) {
            style['--fcp-multiselect-content-width'] = `${contentWidth}px`;
        }
        const listHeight = this.overlayListHeightPx();
        if (listHeight != null) {
            style['--fcp-multiselect-list-height'] = `${listHeight}px`;
        }
        return style;
    });

    onNgModelChange = output<unknown[]>();
    onChange = output<MultiSelectChangeEvent>();
    onFocus = output<FocusEvent>();
    onBlur = output<FocusEvent>();
    onShow = output<void>();
    onHide = output<void>();
    onFilter = output<MultiSelectFilterEvent>();
    onSelectAllChange = output<MultiSelectSelectAllChangeEvent>();
    onLazyLoad = output<MultiSelectLazyLoadEvent>();
    onClear = output<void>();
    onRemove = output<MultiSelectRemoveEvent>();

    private _value = signal<unknown[]>([]);
    private _onChange = signal<(value: unknown[]) => void>(() => {});
    private _onTouched = signal<() => void>(() => {});
    private _formDisabled = signal<boolean>(false);

    model = computed(() => this._value());
    isDisabled = computed(() => this.disabled() || this._formDisabled());
    styleClass = computed(() => {
        const classes = ['fcp-multiselect'];
        if (this.isDisabled()) {
            classes.push('is-disabled');
        }
        return classes.join(' ');
    });

    writeValue(value: unknown[]): void {
        this._value.set(value || []);
    }

    registerOnChange(fn: (value: unknown[]) => void): void {
        this._onChange.set(fn);
    }

    registerOnTouched(fn: () => void): void {
        this._onTouched.set(fn);
    }

    setDisabledState(isDisabled: boolean): void {
        this._formDisabled.set(isDisabled);
    }

    hide(): void {
        if (this.multiSelectRef?.overlayVisible) this.multiSelectRef.hide();
    }

    handlePanelShow(): void {
        const options = this.options();
        const optionLabel = this.optionLabel();
        const group = this.group();
        const optionGroupChildren = this.optionGroupChildren();
        const optionCount = this.countListOptions(options, group, optionGroupChildren);

        if (optionCount === 0) {
            // Opening with nothing to list — match the trigger width; height fits the empty message.
            this.seedOverlayWidthFromTrigger();
            this.seedOverlayHeightForEmpty();
        } else {
            this.syncOverlaySize(
                options,
                optionLabel,
                group,
                optionGroupChildren,
                this.optionGroupLabel(),
                this.virtualScrollItemSize(),
                this.scrollHeight()
            );
        }
        this.onShow.emit();
    }

    handleChange(value: unknown[]): void {
        const newValue = value || [];
        this._value.set(newValue);
        this._onChange()(newValue);
        this.onNgModelChange.emit(newValue);
    }

    handleBlur(event: FocusEvent): void {
        this._onTouched()();
        this.onBlur.emit(event);
    }

    handleFilter(event: MultiSelectFilterEvent): void {
        queueMicrotask(() => {
            const { options, flatRows } = this.getVisibleOptions();
            // PrimeNG visibleOptions is already flat (group headers + matching options).
            // Pass group=false so we count those rows instead of nested children.
            this.syncOverlaySize(
                options,
                this.optionLabel(),
                flatRows ? false : this.group(),
                this.optionGroupChildren(),
                this.optionGroupLabel(),
                this.virtualScrollItemSize(),
                this.scrollHeight()
            );
        });
        this.onFilter.emit(event);
    }

    /**
     * Options currently shown in the overlay. When taken from PrimeNG `visibleOptions`,
     * the list is already flat (group headers + options) even if `group` is true.
     */
    private getVisibleOptions(): { options: unknown[]; flatRows: boolean } {
        const visible = this.multiSelectRef?.visibleOptions?.();
        if (Array.isArray(visible)) {
            return { options: visible, flatRows: true };
        }
        return { options: this.options(), flatRows: false };
    }

    private syncOverlaySize(
        options: unknown[],
        optionLabel: string,
        group: boolean,
        optionGroupChildren: string,
        optionGroupLabel: string,
        itemSize: number,
        scrollHeight: string
    ): void {
        if (typeof document === 'undefined') {
            return;
        }

        const optionCount = this.countListOptions(options, group, optionGroupChildren);
        if (optionCount === 0) {
            // Empty search: keep width from panel open / last options; shrink height to the empty message.
            this.seedOverlayHeightForEmpty();
            return;
        }

        const labels = this.collectOptionLabels(options, optionLabel, group, optionGroupChildren, optionGroupLabel);
        const triggerWidth = this.getTriggerWidth();
        const longestLabelWidth = this.measureLongestLabelWidth(labels);
        const contentWidth = longestLabelWidth + this.getOptionChromeWidth();
        const width = this.resolveOverlayWidth(triggerWidth, contentWidth);

        const maxListHeight = this.parseCssLengthPx(scrollHeight, 200);
        const listHeight = Math.min(maxListHeight, optionCount * itemSize);

        this.overlayContentWidthPx.set(Math.ceil(width));
        this.overlayListHeightPx.set(Math.ceil(listHeight));
        this.realignOpenOverlay();
    }

    private seedOverlayWidthFromTrigger(): void {
        if (typeof document === 'undefined') {
            return;
        }
        const triggerWidth = this.getTriggerWidth();
        if (triggerWidth <= 0) {
            return;
        }
        this.overlayContentWidthPx.set(Math.ceil(this.resolveOverlayWidth(triggerWidth, triggerWidth)));
        this.realignOpenOverlay();
    }

    /**
     * Match the trigger width. Grow for long labels up to 480px only when the trigger is
     * narrower than that cap. If a wider-than-trigger panel cannot fit left-aligned under
     * the field, match the trigger instead so DomHandler does not shift the overlay past
     * the field edges (padded small screens).
     */
    private resolveOverlayWidth(triggerWidth: number, contentWidth: number): number {
        // Cap applies to content-driven growth, not to matching a wider trigger.
        const width = Math.max(triggerWidth, Math.min(contentWidth, OVERLAY_MAX_WIDTH_PX));

        if (typeof window === 'undefined') {
            return width;
        }

        const triggerLeft = this.getTriggerElement()?.getBoundingClientRect().left ?? 0;
        const spaceRight = Math.max(0, window.innerWidth - triggerLeft);

        if (width > spaceRight) {
            return Math.min(triggerWidth, spaceRight);
        }
        return Math.min(width, spaceRight);
    }

    private seedOverlayHeightForEmpty(): void {
        this.overlayListHeightPx.set(this.virtualScrollItemSize());
        this.realignOpenOverlay();
    }

    private countListOptions(options: unknown[], group: boolean, optionGroupChildren: string): number {
        if (!group) {
            return options.length;
        }
        let count = 0;
        for (const option of options) {
            if (option && typeof option === 'object') {
                const children = (option as Record<string, unknown>)[optionGroupChildren];
                if (Array.isArray(children)) {
                    count += children.length;
                }
            }
        }
        return count;
    }

    private parseCssLengthPx(value: string, fallback: number): number {
        const trimmed = value.trim();
        const amount = Number.parseFloat(trimmed);
        if (!Number.isFinite(amount)) {
            return fallback;
        }
        if (trimmed.endsWith('rem')) {
            const rootFontSize =
                typeof document !== 'undefined'
                    ? Number.parseFloat(getComputedStyle(document.documentElement).fontSize) || 16
                    : 16;
            return amount * rootFontSize;
        }
        if (trimmed.endsWith('vh')) {
            if (typeof window === 'undefined' || !Number.isFinite(window.innerHeight)) {
                return fallback;
            }
            return (amount / 100) * window.innerHeight;
        }
        return amount;
    }

    private collectOptionLabels(
        options: unknown[],
        optionLabel: string,
        group: boolean,
        optionGroupChildren: string,
        optionGroupLabel: string
    ): string[] {
        const labels: string[] = [];
        for (const option of options) {
            if (group && option && typeof option === 'object') {
                const groupLabel = this.resolveOptionLabel(option, optionGroupLabel);
                if (groupLabel) {
                    labels.push(groupLabel);
                }
                const children = (option as Record<string, unknown>)[optionGroupChildren];
                if (Array.isArray(children)) {
                    labels.push(
                        ...this.collectOptionLabels(children, optionLabel, false, optionGroupChildren, optionGroupLabel)
                    );
                }
                continue;
            }
            // PrimeNG flat visibleOptions uses { optionGroup, group: true } header rows.
            if (this.isFlatGroupHeader(option)) {
                const groupLabel = this.resolveOptionLabel(
                    (option as Record<string, unknown>)['optionGroup'],
                    optionGroupLabel
                );
                if (groupLabel) {
                    labels.push(groupLabel);
                }
                continue;
            }
            const label = this.resolveOptionLabel(option, optionLabel);
            if (label) {
                labels.push(label);
            }
        }
        return labels;
    }

    /** PrimeNG `flatOptions` / `visibleOptions` group-header wrapper shape. */
    private isFlatGroupHeader(option: unknown): boolean {
        if (!option || typeof option !== 'object') {
            return false;
        }
        const record = option as Record<string, unknown>;
        return !!record['optionGroup'] && record['group'] === true;
    }

    private resolveOptionLabel(option: unknown, optionLabel: string): string {
        if (option == null) {
            return '';
        }
        if (typeof option === 'string' || typeof option === 'number') {
            return String(option);
        }
        if (typeof option === 'object') {
            const record = option as Record<string, unknown>;
            const value = optionLabel ? record[optionLabel] : (record['label'] ?? option);
            return value == null ? '' : String(value);
        }
        return '';
    }

    private measureLongestLabelWidth(labels: string[]): number {
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        if (!context) {
            return 0;
        }

        const rootStyle = getComputedStyle(document.documentElement);
        const bodyStyle = getComputedStyle(document.body);
        const fontSize =
            rootStyle.getPropertyValue('--typography-body-m-size-regular').trim() || bodyStyle.fontSize || '14px';
        const fontWeight =
            rootStyle.getPropertyValue('--typography-body-m-weight-regular').trim() || bodyStyle.fontWeight || '400';
        const fontFamily = bodyStyle.fontFamily || 'sans-serif';
        context.font = `${fontWeight} ${fontSize} ${fontFamily}`;

        let longest = 0;
        for (const label of labels) {
            longest = Math.max(longest, context.measureText(label).width);
        }
        return longest;
    }

    /** Checkbox + gap + option padding-x + list padding (design tokens). */
    private getOptionChromeWidth(): number {
        const sizing = (token: string, fallback: number) => {
            const raw = getComputedStyle(document.documentElement).getPropertyValue(token).trim();
            const parsed = Number.parseFloat(raw);
            return Number.isFinite(parsed) ? parsed : fallback;
        };
        const checkbox = sizing('--sizing-20', 20);
        const gap = sizing('--sizing-14', 14);
        const optionPaddingX = sizing('--sizing-12', 12) * 2;
        const listPaddingX = sizing('--sizing-4', 4) * 2;
        return checkbox + gap + optionPaddingX + listPaddingX;
    }

    private getTriggerElement(): HTMLElement | null {
        return this.host.nativeElement.querySelector('.p-multiselect') as HTMLElement | null;
    }

    private getTriggerWidth(): number {
        return this.getTriggerElement()?.offsetWidth || this.host.nativeElement.offsetWidth || 0;
    }

    private realignOpenOverlay(): void {
        if (!this.multiSelectRef?.overlayVisible) {
            return;
        }
        requestAnimationFrame(() => {
            this.multiSelectRef?.overlayViewChild?.alignOverlay();
        });
    }
}

export enum MultiSelectDisplay {
    Comma = 'comma',
    Chip = 'chip'
}

export enum MultiSelectFilterMatchMode {
    Contains = 'contains',
    StartsWith = 'startsWith',
    EndsWith = 'endsWith'
}

export enum MultiSelectSizes {
    Small = 'small',
    Normal = null
}
